/**
 * Pulls a CosmicTyper-friendly word list out of a book description.
 *
 * Rules baked in here so the call site stays tidy:
 *   1. Tokenise on anything that isn't a-z (case-insensitive). Contractions,
 *      hyphenated compounds, and punctuation are split into pieces — the
 *      game's own sanitizer would drop them anyway because it requires
 *      `/^[a-z]+$/`.
 *   2. Lowercase everything (CosmicTyper sanitizes to lowercase too — doing
 *      it here lets the dedupe step actually work).
 *   3. Drop common stop-words and very short words. We're after typing
 *      practice, not "the" / "and" / "of" 30 times.
 *   4. Deduplicate while preserving the first occurrence.
 *   5. Sort by length descending, then alphabetically as a tiebreaker so
 *      the queue is deterministic for the same description.
 *   6. Take the first N (default 30).
 *
 * Returns an empty array for missing/empty/short descriptions; the modal
 * uses that to hide the CTA entirely rather than launch a game with a
 * three-word queue.
 */
const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'at',
  'be',
  'but',
  'by',
  'do',
  'for',
  'from',
  'had',
  'has',
  'have',
  'he',
  'her',
  'his',
  'i',
  'if',
  'in',
  'into',
  'is',
  'it',
  'its',
  'of',
  'on',
  'or',
  'so',
  'than',
  'that',
  'the',
  'their',
  'them',
  'then',
  'there',
  'they',
  'this',
  'to',
  'too',
  'was',
  'we',
  'were',
  'what',
  'when',
  'which',
  'who',
  'will',
  'with',
  'you',
  'your',
]);

/** Minimum word length we accept. Three letters is too easy and adds noise. */
const MIN_LENGTH = 4;

export function extractBookWords(description: string | null | undefined, count = 30): string[] {
  if (!description) return [];

  const tokens = description
    .toLowerCase()
    .split(/[^a-z]+/)
    .filter((w) => w.length >= MIN_LENGTH && !STOP_WORDS.has(w));

  if (tokens.length === 0) return [];

  // Dedupe while keeping insertion order — `Set` already does this for us.
  const unique = Array.from(new Set(tokens));

  // Sort by length desc → "biggest words first". Alphabetical tiebreaker
  // makes the result stable across renders for the same description, which
  // matters because CosmicTyper re-shuffles whenever the `words` prop
  // reference changes.
  unique.sort((a, b) => b.length - a.length || a.localeCompare(b));

  return unique.slice(0, count);
}
